import React, { useEffect, useState } from 'react';
import OwnRefPartyCell from '../components/OwnRefPartyCell';
import Card from '../components/Card';
import Button from '../components/Button';
import FormField, { inputStyle } from '../components/FormField';
import * as usersApi from '../services/usersApi';

function StatusBadge({ status }) {
  const colors = {
    active: 'var(--color-success)',
    blocked: 'var(--color-danger)',
    invited: 'var(--color-warning)',
  };
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        fontSize: 'var(--font-size-xs)',
        fontWeight: 500,
        color: colors[status] || 'var(--color-text-secondary)',
      }}
    >
      <span
        style={{
          width: 8,
          height: 8,
          borderRadius: '50%',
          background: colors[status] || 'var(--color-text-muted)',
          display: 'inline-block',
        }}
      />
      {status}
    </span>
  );
}

/** Inline "set a new password directly" row — separate from the self-service
 * OTP-based Forgot Password flow (that's for a user to use on their own
 * later); this is the admin handing someone a password now. */
function ResetPasswordRow({ user, onDone, onCancel }) {
  const [newPassword, setNewPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await usersApi.resetUserPassword(user.user_id, newPassword);
      onDone();
    } catch (err) {
      setError(err.message || 'Could not set the password.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <tr style={{ background: 'var(--color-surface-alt)' }}>
      <td colSpan={5} style={{ padding: 'var(--space-3)' }}>
        <form onSubmit={handleSubmit} style={{ display: 'flex', gap: 'var(--space-3)', alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <FormField label={`New password for ${user.full_name} (min. 10 characters)`}>
            <input
              type="password"
              required
              minLength={10}
              autoFocus
              style={inputStyle}
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
            />
          </FormField>
          <div style={{ display: 'flex', gap: 'var(--space-2)', marginBottom: 'var(--space-4)' }}>
            <Button type="submit" disabled={submitting}>{submitting ? 'Saving…' : 'Set password'}</Button>
            <Button variant="text" type="button" onClick={onCancel}>Cancel</Button>
          </div>
        </form>
        {error && <p style={{ color: 'var(--color-danger)', fontSize: 'var(--font-size-sm)', margin: 0 }}>{error}</p>}
        <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)', margin: 'var(--space-2) 0 0' }}>
          This signs {user.full_name} out everywhere. They can change it again anytime from
          &quot;Forgot password?&quot; on the sign-in page.
        </p>
      </td>
    </tr>
  );
}

/** Inline "limited access" picker — lets an admin grant a specific user
 * exactly the modules/actions they choose, instead of their role's usual
 * full set. Starts pre-checked with whatever the user currently has
 * (custom set if one exists, otherwise their role's own defaults), so
 * opening this never looks like a blank slate. */
function PermissionsRow({ user, onDone, onCancel }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [allPermissions, setAllPermissions] = useState([]);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [isCustom, setIsCustom] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    usersApi.getUserPermissions(user.user_id)
      .then((data) => {
        if (cancelled) return;
        setAllPermissions(data.allPermissions);
        setSelectedIds(new Set(data.grantedPermissionIds));
        setIsCustom(data.isCustom);
      })
      .catch((err) => setError(err.message || 'Could not load permissions.'))
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [user.user_id]);

  const grouped = allPermissions.reduce((acc, p) => {
    (acc[p.module_code] = acc[p.module_code] || { name: p.module_name, items: [] }).items.push(p);
    return acc;
  }, {});

  const toggle = (permissionId) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(permissionId)) next.delete(permissionId); else next.add(permissionId);
      return next;
    });
  };

  const toggleModule = (items, checkAll) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      items.forEach((p) => { if (checkAll) next.add(p.permission_id); else next.delete(p.permission_id); });
      return next;
    });
  };

  const handleSave = async () => {
    setError(null);
    setSaving(true);
    try {
      await usersApi.setUserPermissions(user.user_id, { permissionIds: Array.from(selectedIds) });
      onDone();
    } catch (err) {
      setError(err.message || 'Could not save access.');
    } finally {
      setSaving(false);
    }
  };

  const handleUseRoleDefaults = async () => {
    setError(null);
    setSaving(true);
    try {
      await usersApi.setUserPermissions(user.user_id, { useRoleDefaults: true });
      onDone();
    } catch (err) {
      setError(err.message || 'Could not reset access.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <tr style={{ background: 'var(--color-surface-alt)' }}>
      <td colSpan={5} style={{ padding: 'var(--space-4)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 'var(--space-2)' }}>
          <div>
            <strong>Access for {user.full_name}</strong>
            <span style={{ marginLeft: 8, fontSize: 'var(--font-size-xs)', color: isCustom ? 'var(--color-accent)' : 'var(--color-text-muted)' }}>
              {isCustom ? 'Custom access' : `Using "${user.role_name}" role defaults`}
            </span>
          </div>
          <Button variant="text" onClick={onCancel}>Close</Button>
        </div>

        {loading && <p style={{ color: 'var(--color-text-secondary)' }}>Loading…</p>}
        {error && <p style={{ color: 'var(--color-danger)', fontSize: 'var(--font-size-sm)' }}>{error}</p>}

        {!loading && (
          <>
            <div style={{ maxHeight: 320, overflowY: 'auto', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-sm)', padding: 'var(--space-3)' }}>
              {Object.entries(grouped).map(([moduleCode, mod]) => {
                const allChecked = mod.items.every((p) => selectedIds.has(p.permission_id));
                const someChecked = mod.items.some((p) => selectedIds.has(p.permission_id));
                return (
                  <div key={moduleCode} style={{ marginBottom: 'var(--space-3)' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontWeight: 600, fontSize: 'var(--font-size-sm)', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={allChecked}
                        ref={(el) => { if (el) el.indeterminate = someChecked && !allChecked; }}
                        onChange={(e) => toggleModule(mod.items, e.target.checked)}
                      />
                      {mod.name}
                    </label>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginLeft: 22, marginTop: 4 }}>
                      {mod.items.map((p) => (
                        <label key={p.permission_id} style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)', cursor: 'pointer' }}>
                          <input
                            type="checkbox"
                            checked={selectedIds.has(p.permission_id)}
                            onChange={() => toggle(p.permission_id)}
                          />
                          {p.action}
                        </label>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
            <div style={{ display: 'flex', gap: 'var(--space-2)', marginTop: 'var(--space-3)' }}>
              <Button onClick={handleSave} disabled={saving}>{saving ? 'Saving…' : 'Save custom access'}</Button>
              <Button variant="text" onClick={handleUseRoleDefaults} disabled={saving}>
                Use role defaults instead
              </Button>
            </div>
          </>
        )}
      </td>
    </tr>
  );
}

export default function UsersPage() {
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    fullName: '',
    email: '',
    phone: '',
    roleId: '',
    refPartyId: '',
    temporaryPassword: '',
  });
  const [formError, setFormError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [resettingId, setResettingId] = useState(null);
  const [permissionsId, setPermissionsId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [refAccess, setRefAccess] = useState({});
  const [watermarks, setWatermarks] = useState({});
  const [refParties, setRefParties] = useState([]);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const [usersData, rolesData, accessData] = await Promise.all([
        usersApi.listUsers(),
        usersApi.listRoles(),
        usersApi.listRefPartyAccess().catch(() => ({ assignments: [], refParties: [] })),
      ]);
      setUsers(usersData.items);
      setRoles(rolesData);
      setRefParties(accessData.refParties || []);
      setRefAccess(Object.fromEntries((accessData.assignments || []).map((a) => [a.userId, a.refPartyId])));
      setWatermarks(Object.fromEntries((accessData.watermarks || []).map((w) => [w.userId, w.enabled])));
    } catch (err) {
      setError(err.message || 'Could not load users.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    setFormError(null);
    setSubmitting(true);
    try {
      const { refPartyId: chosenRefParty, ...newUser } = form;
      await usersApi.createUser({ ...newUser, roleId: Number(form.roleId) });
      if (chosenRefParty) {
        // The create call does not take a Ref. Party; look the new user up and limit them right away.
        const fresh = await usersApi.listUsers();
        const created = fresh.items.find((x) => x.email.toLowerCase() === form.email.trim().toLowerCase());
        if (created) await usersApi.setUserRefParty(created.user_id, Number(chosenRefParty));
      }
      setForm({ fullName: '', email: '', phone: '', roleId: '', refPartyId: '', temporaryPassword: '' });
      setShowForm(false);
      await load();
    } catch (err) {
      setFormError(err.message || 'Could not create user.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRefPartyChange = async (targetUser, value) => {
    setError(null);
    try {
      await usersApi.setUserRefParty(targetUser.user_id, value ? Number(value) : null);
      await load();
    } catch (err) {
      setError(err.message || 'Could not update Ref. Party access.');
    }
  };

  const handleWatermarkChange = async (targetUser, enabled) => {
    setError(null);
    try {
      await usersApi.setUserWatermark(targetUser.user_id, enabled);
      await load();
    } catch (err) {
      setError(err.message || 'Could not update watermark.');
    }
  };

  const toggleStatus = async (targetUser) => {
    const nextStatus = targetUser.status === 'blocked' ? 'active' : 'blocked';
    try {
      await usersApi.setUserStatus(targetUser.user_id, nextStatus);
      await load();
    } catch (err) {
      setError(err.message || 'Could not update user status.');
    }
  };

  const handleDelete = async (targetUser) => {
    setDeletingId(targetUser.user_id);
    setError(null);
    try {
      await usersApi.deleteUser(targetUser.user_id);
      setConfirmDeleteId(null);
      await load();
    } catch (err) {
      setError(err.message || 'Could not delete user.');
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div>
      <h1>User Management</h1>
      <p style={{ color: 'var(--color-text-secondary)' }}>
        Create accounts, assign roles, set a starting password, give someone limited access to
        just the modules they need, or remove users in your company.
      </p>

      <Card
        title="Users"
        actions={
          <Button onClick={() => setShowForm((s) => !s)}>
            {showForm ? 'Cancel' : 'Add user'}
          </Button>
        }
      >
        {showForm && (
          <form
            onSubmit={handleCreate}
            onKeyDown={(e) => {
              if (e.key === 'Escape') setShowForm(false);
            }}
            style={{
              borderBottom: '1px solid var(--color-border)',
              paddingBottom: 'var(--space-4)',
              marginBottom: 'var(--space-4)',
            }}
          >
            {formError && (
              <p style={{ color: 'var(--color-danger)', fontSize: 'var(--font-size-sm)' }}>
                {formError}
              </p>
            )}
            <div
              className="responsive-grid-2col"
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: 'var(--space-4)',
              }}
            >
              <FormField label="Full name">
                <input
                  required
                  style={inputStyle}
                  value={form.fullName}
                  onChange={(e) => setForm({ ...form, fullName: e.target.value })}
                />
              </FormField>
              <FormField label="Email">
                <input
                  type="email"
                  required
                  style={inputStyle}
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                />
              </FormField>
              <FormField label="Phone (optional)">
                <input
                  style={inputStyle}
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                />
              </FormField>
              <FormField label="Role">
                <select
                  required
                  style={inputStyle}
                  value={form.roleId}
                  onChange={(e) => setForm({ ...form, roleId: e.target.value })}
                >
                  <option value="">Select a role…</option>
                  {roles.map((r) => (
                    <option key={r.role_id} value={r.role_id}>
                      {r.name}
                    </option>
                  ))}
                </select>
              </FormField>
              <FormField label="Ref. Party access">
                <select
                  style={inputStyle}
                  value={form.refPartyId}
                  onChange={(e) => setForm({ ...form, refPartyId: e.target.value })}
                >
                  <option value="">All Ref. Parties (full access)</option>
                  {refParties.map((p) => (
                    <option key={p.id} value={p.id}>
                      Only {p.name}
                    </option>
                  ))}
                </select>
              </FormField>
              <FormField label="Starting password (min. 10 characters)">
                <input
                  type="password"
                  required
                  minLength={10}
                  style={inputStyle}
                  value={form.temporaryPassword}
                  onChange={(e) => setForm({ ...form, temporaryPassword: e.target.value })}
                />
              </FormField>
            </div>
            <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>
              Share this password with them directly. They can set their own later from
              &quot;Forgot password?&quot; on the sign-in page. You can give them limited access to
              specific modules afterward using &quot;Access&quot; below.
            </p>
            <Button type="submit" disabled={submitting} style={{ marginTop: 'var(--space-2)' }}>
              {submitting ? 'Creating…' : 'Create user'}
            </Button>
          </form>
        )}

        {loading && <p style={{ color: 'var(--color-text-secondary)' }}>Loading users…</p>}
        {error && <p style={{ color: 'var(--color-danger)' }}>{error}</p>}

        {!loading && !error && (
          <div className="table-scroll">
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ textAlign: 'left', borderBottom: '1px solid var(--color-border)' }}>
                <th style={{ padding: '8px 4px', fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>Name</th>
                <th style={{ padding: '8px 4px', fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>Email</th>
                <th style={{ padding: '8px 4px', fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>Role</th>
                <th style={{ padding: '8px 4px', fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>Ref. Party access</th>
                <th style={{ padding: '8px 4px', fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }} title="If a full-access user is also a Ref. Party, choose it: they get a My report switch">Own Ref. Party (My report)</th>
                <th style={{ padding: '8px 4px', fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>Watermark</th>
                <th style={{ padding: '8px 4px', fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>Status</th>
                <th style={{ padding: '8px 4px' }} />
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <React.Fragment key={u.user_id}>
                  <tr style={{ borderBottom: '1px solid var(--color-border)' }}>
                    <td style={{ padding: '10px 4px' }}>{u.full_name}</td>
                    <td className="data-figure" style={{ padding: '10px 4px' }}>{u.email}</td>
                    <td style={{ padding: '10px 4px' }}>{u.role_name}</td>
                    <td style={{ padding: '10px 4px' }}>
                      <select
                        style={{ ...inputStyle, minWidth: 170 }}
                        value={refAccess[u.user_id] || ''}
                        onChange={(e) => handleRefPartyChange(u, e.target.value)}
                      >
                        <option value="">All Ref. Parties</option>
                        {refParties.map((p) => (
                          <option key={p.id} value={p.id}>
                            Only {p.name}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td style={{ padding: '10px 4px' }}>
                      <OwnRefPartyCell userId={u.user_id} refParties={refParties} locked={Boolean(refAccess[u.user_id])} style={inputStyle} />
                    </td>
                    <td style={{ padding: '10px 4px' }}>
                      <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer' }} title="Show a name watermark and block print / screenshot keys for this user">
                        <input
                          type="checkbox"
                          checked={Boolean(watermarks[u.user_id])}
                          onChange={(e) => handleWatermarkChange(u, e.target.checked)}
                        />
                        <span style={{ fontSize: 'var(--font-size-xs)' }}>{watermarks[u.user_id] ? 'On' : 'Off'}</span>
                      </label>
                    </td>
                    <td style={{ padding: '10px 4px' }}>
                      <StatusBadge status={u.status} />
                    </td>
                    <td style={{ padding: '10px 4px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                      <Button variant="text" onClick={() => toggleStatus(u)}>
                        {u.status === 'blocked' ? 'Unblock' : 'Block'}
                      </Button>
                      <Button
                        variant="text"
                        onClick={() => setResettingId(resettingId === u.user_id ? null : u.user_id)}
                      >
                        {resettingId === u.user_id ? 'Cancel' : 'Set password'}
                      </Button>
                      <Button
                        variant="text"
                        onClick={() => setPermissionsId(permissionsId === u.user_id ? null : u.user_id)}
                      >
                        {permissionsId === u.user_id ? 'Cancel' : 'Access'}
                      </Button>
                      {confirmDeleteId === u.user_id ? (
                        <>
                          <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-danger)', marginRight: 4 }}>
                            Delete {u.full_name}?
                          </span>
                          <Button
                            variant="text"
                            disabled={deletingId === u.user_id}
                            onClick={() => handleDelete(u)}
                            style={{ color: 'var(--color-danger)' }}
                          >
                            {deletingId === u.user_id ? 'Deleting…' : 'Yes, delete'}
                          </Button>
                          <Button variant="text" onClick={() => setConfirmDeleteId(null)}>No</Button>
                        </>
                      ) : (
                        <Button
                          variant="text"
                          onClick={() => setConfirmDeleteId(u.user_id)}
                          style={{ color: 'var(--color-danger)' }}
                        >
                          Delete
                        </Button>
                      )}
                    </td>
                  </tr>
                  {resettingId === u.user_id && (
                    <ResetPasswordRow
                      user={u}
                      onDone={() => { setResettingId(null); load(); }}
                      onCancel={() => setResettingId(null)}
                    />
                  )}
                  {permissionsId === u.user_id && (
                    <PermissionsRow
                      user={u}
                      onDone={() => { setPermissionsId(null); load(); }}
                      onCancel={() => setPermissionsId(null)}
                    />
                  )}
                </React.Fragment>
              ))}
              {users.length === 0 && (
                <tr>
                  <td colSpan={7} style={{ padding: 'var(--space-4)', color: 'var(--color-text-muted)' }}>
                    No users yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
          </div>
        )}
      </Card>
    </div>
  );
}
